import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Tabs, { TabList } from '@atlaskit/tabs';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { type SendDatabaseCreatedViewEventData } from '@databases/analytics/src/types';
import { messages } from '@databases/api-views/messages';
import { isEmbed } from '@databases/modes';
import usePanel from '@databases/panel/usePanel';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { type DatabaseView } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import { getDefaultView } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { type ViewPanelSettingType } from '@databases/views/types';
import useView from '@databases/views/useView';
import afterFrame from 'afterframe';
import { debounce } from 'lodash-es';
import React, {
  type FunctionComponent,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  createRef,
  type RefObject,
} from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

import AddViewButton from './AddViewButton';
import { InstanceIdContext, DraggableViewTab } from './DraggableViewTab';

function getInstanceId() {
  return Symbol('instance-id');
}

// this is used to display tabs in a way that is visually hidden but still
// allows us to calculate the width of the tab
const VisuallyHidden = styled.div`
  visibility: hidden;
  position: absolute;
`;

export interface ViewTabsProps {
  contentId: string;
  defaultView?: DatabaseView;
  isReadonly: boolean;
  openViewSettingsPanel: (activeViewSetting: ViewPanelSettingType | null) => void;
  handleAddView: (source: SendDatabaseCreatedViewEventData['source']) => void;
  showViewSettings?: boolean;

  // TODO NCTD-1985 remove this prop once the view picker is extracted
  ViewPicker: React.FunctionComponent<any>;

  // TODO NCTD-1989 remove these props after we release the new views experience
  isViewsMenuOpen: boolean;
  setIsViewsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const ViewTabs: FunctionComponent<ViewTabsProps> = ({
  contentId,
  defaultView,
  isReadonly,
  openViewSettingsPanel,
  handleAddView,
  showViewSettings = true,
  ViewPicker,
  isViewsMenuOpen,
  setIsViewsMenuOpen,
}) => {
  const [instanceId] = useState(getInstanceId);

  const { view: selectedView, setViewId, unsavedView } = useView();
  const dispatch = defaultStoreUtils.useDispatch();
  const client = useAnalytics();
  const { hidePanel } = usePanel();
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
  const [draggedOverIndex, setDraggedOverIndex] = useState<number | null>(null);

  const [savedViews, databaseDefaultView] = defaultStoreUtils.useEqualSelector(
    (state) => {
      const database = selectDatabaseByContentIdRequired(state, { contentId });
      return [database.views, getDefaultView(database)] as const;
    }
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const viewTabsActionsRef = useRef<HTMLDivElement>(null);

  const [containerWidth, setContainerWidth] = useState(0);

  const resizeObserver = useMemo(() => {
    const updateContainerWidth = debounce(() => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    }, 50);
    const observer = new ResizeObserver(() => {
      updateContainerWidth();
    });
    return observer;
  }, []);

  useEffect(() => {
    if (!savedViews) {
      return;
    }

    const startedRenderingAt = performance.now();
    afterFrame(() => {
      const reliability = ReliabilityMetrics.getInstance();
      reliability.successWithDuration(
        MetricName.DATABASE_TOOLBAR_TOOLBAR_RENDER_VIEWS,
        startedRenderingAt,
        performance.now()
      );
    });
  }, [savedViews]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      resizeObserver.observe(container);
    }
    return () => {
      if (container) {
        resizeObserver.unobserve(container);
      }
    };
  }, [resizeObserver]);

  const currentViews = useMemo(
    () =>
      savedViews.map((view) => {
        return view.id === selectedView.id ? selectedView : view;
      }),
    [selectedView, savedViews]
  );

  const tabRefs = useMemo(() => {
    const refs: Record<string, RefObject<HTMLDivElement>> = {};
    currentViews.forEach((view) => {
      refs[view.id] = createRef();
    });
    if (unsavedView) {
      refs[unsavedView.id] = createRef();
    }
    return refs;
  }, [currentViews, unsavedView]);

  const [visibleTabCount, setVisibleTabCount] = useState(0);
  const [isSelectedViewInOverflow, setIsSelectedViewInOverflow] = useState(false);
  const [showUnsavedView, setShowUnsavedView] = useState(true);
  const [isOverflow, setIsOverflow] = useState(false);

  useEffect(() => {
    const actionsWidth = viewTabsActionsRef.current?.clientWidth;
    const selectedViewWidth = tabRefs[selectedView.id]?.current?.clientWidth;

    // Fallback to direct clientWidth if ResizeObserver hasn't fired yet
    const effectiveContainerWidth =
      containerWidth !== 0 ? containerWidth : containerRef.current?.clientWidth || 0;

    if (
      effectiveContainerWidth != 0 &&
      actionsWidth !== undefined &&
      selectedViewWidth !== undefined
    ) {
      let tabsWidth = selectedViewWidth + actionsWidth;
      let visibleTabCount = 1; // always include selected view
      let currentViewSeen = false;
      if (unsavedView !== null && tabRefs[unsavedView.id].current) {
        const unsavedViewWidth = tabRefs[unsavedView?.id].current?.clientWidth;
        if (selectedView.id != unsavedView.id) {
          tabsWidth += unsavedViewWidth ?? 0;
          if (tabsWidth > effectiveContainerWidth) {
            setShowUnsavedView(false);
          } else {
            setShowUnsavedView(true);
          }
        } else {
          setShowUnsavedView(true);
        }
      }
      for (const view of currentViews) {
        const tabRef = tabRefs[view.id].current;
        if (tabRef) {
          if (view.id !== selectedView.id) {
            tabsWidth += tabRef.clientWidth;
            if (tabsWidth > effectiveContainerWidth) {
              break;
            }
            visibleTabCount++;
          } else {
            currentViewSeen = true;
          }
        }
      }
      setVisibleTabCount(visibleTabCount);
      setIsSelectedViewInOverflow(!currentViewSeen);
      setIsOverflow(currentViews.length > visibleTabCount);
    }
  }, [containerWidth, selectedView.id, currentViews, tabRefs, unsavedView]);

  const { formatMessage } = useIntl();
  const selectedIdx =
    savedViews.findIndex((view) => view.id === selectedView.id) + (unsavedView ? 1 : 0);

  const dispatchReorder = useCallback(
    (viewId: string, newIndex: number) => {
      dispatch(
        addChangeToDraft(contentId, 'main', {
          type: DatabaseChangeType.REORDER_SAVED_VIEW,
          viewId,
          newIndex,
        })
      );
      client.sendDatabaseViewReorderedEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        viewId,
        source: 'databaseViewTabs',
      });
    },
    [client, contentId, dispatch]
  );

  const handleTabReorder = useCallback(
    (idx: number, newIdx: number) => {
      if (closestEdge) {
        const shift = closestEdge === 'right' ? 1 : 0;
        dispatchReorder(savedViews[idx].id, newIdx + shift);
      }
    },
    [closestEdge, dispatchReorder, savedViews]
  );

  const handleReorder = useCallback(
    (idx: number, newIdx: number) => {
      // our changeset expects the index before removing the item
      dispatchReorder(savedViews[idx].id, newIdx + (newIdx > idx ? 1 : 0));
    },
    [dispatchReorder, savedViews]
  );

  useEffect(() => {
    return monitorForElements({
      canMonitor({ source }) {
        return source.data.instanceId === instanceId;
      },
      onDrop({ source, location }) {
        const destination = location.current.dropTargets[0];
        if (!destination) {
          return;
        }
        const destinationContent = destination.data.view as DatabaseView;
        const startContent = source.data.view as DatabaseView;
        if (source.data.type !== 'tab' || !destinationContent || !startContent) {
          return;
        }

        const idx = savedViews.findIndex((tab) => tab.id === startContent.id);
        const newIdx = savedViews.findIndex((view) => view.id === destinationContent.id);

        if (isSelectedViewInOverflow && destinationContent.id === selectedView.id) {
          // if the selected view is in overflow and shown as the last visible
          // tab, newIdx will not be accurate as it reflects the index of the
          // view in the model and not the visible tabs, so we need to manually
          // move the selected view and the dragged view
          const selectedViewIdx = visibleTabCount - 1;
          if (closestEdge === 'right') {
            dispatchReorder(selectedView.id, selectedViewIdx);
            dispatchReorder(startContent.id, selectedViewIdx + 1);
          } else {
            dispatchReorder(startContent.id, selectedViewIdx);
          }
        } else {
          handleTabReorder(idx, newIdx);
        }
      },
    });
  }, [
    selectedView.id,
    dispatchReorder,
    handleTabReorder,
    instanceId,
    isSelectedViewInOverflow,
    savedViews,
    visibleTabCount,
    closestEdge,
  ]);

  const handleViewClick = useCallback(
    (view: DatabaseView) => {
      if (view.id !== selectedView.id) {
        setViewId(view.id);
        afterFrame(() => {
          hidePanel();
        });
      }

      client.sendDatabaseViewSelected({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
        viewId: view.id,
        source: 'databaseViewTabs',
      });
    },
    [selectedView.id, client, contentId, hidePanel, setViewId]
  );

  const onChangeTab = useCallback(
    (index: number) => {
      const startedViewChangeAt = performance.now();
      if (unsavedView) {
        if (index === 0) {
          handleViewClick(unsavedView);
        } else {
          handleViewClick(savedViews[index - 1]);
        }
      } else {
        handleViewClick(savedViews[index]);
      }

      afterFrame(() => {
        const reliability = ReliabilityMetrics.getInstance();
        reliability.successWithDuration(
          MetricName.DATABASE_TOOLBAR_VIEWS_CHANGE_VIEW,
          startedViewChangeAt,
          performance.now()
        );
      });
    },
    [handleViewClick, savedViews, unsavedView]
  );

  const UnsavedViewWrapper = showUnsavedView ? 'div' : VisuallyHidden;

  return (
    <Inline
      alignBlock="center"
      grow="fill"
      xcss={xcss({
        overflow: 'hidden',
      })}
      testId="view-tabs-container"
      ref={containerRef}
    >
      <InstanceIdContext.Provider value={instanceId}>
        <Box
          xcss={xcss({
            width: 'max-content',
          })}
        >
          <Tabs id="view-tabs" onChange={onChangeTab} selected={selectedIdx}>
            <TabList>
              {unsavedView && (
                <UnsavedViewWrapper ref={tabRefs[unsavedView.id]}>
                  <DraggableViewTab
                    view={unsavedView}
                    key={unsavedView.id}
                    contentId={contentId}
                    isActive={unsavedView.id === selectedView.id}
                    isDefault={isEmbed()}
                    isReadonly={isReadonly}
                    openViewSettingsPanel={openViewSettingsPanel}
                    showViewSettings={showViewSettings}
                    isUnsavedView
                    draggingProps={{
                      tabIndex: 0,
                      closestEdge,
                      setClosestEdge,
                      draggedOverIndex,
                      setDraggedOverIndex,
                    }}
                    reorderingProps={{
                      handleReorder,
                      isRightmostTab: showUnsavedView && visibleTabCount === 1,
                    }}
                  />
                </UnsavedViewWrapper>
              )}
              {currentViews.map((view, i) => {
                const isSelected = view.id === selectedView.id;
                const maxVisibleTabIndex = isSelectedViewInOverflow
                  ? visibleTabCount - 1
                  : visibleTabCount;

                const Wrapper =
                  visibleTabCount != 0 && (isSelected || i < maxVisibleTabIndex)
                    ? 'div'
                    : VisuallyHidden;

                const isDefault = isEmbed()
                  ? unsavedView
                    ? false
                    : view.id === (defaultView ?? databaseDefaultView).id
                  : view.id === databaseDefaultView.id;

                return (
                  <Wrapper
                    ref={tabRefs[view.id]}
                    key={`${view.id}-${i + (unsavedView ? 1 : 0)}`}
                    data-testid={`view-tab-${i + (unsavedView ? 1 : 0)}`}
                  >
                    <DraggableViewTab
                      view={view}
                      contentId={contentId}
                      isActive={isSelected}
                      isDefault={isDefault}
                      isReadonly={isReadonly}
                      openViewSettingsPanel={openViewSettingsPanel}
                      showViewSettings={showViewSettings}
                      draggingProps={{
                        tabIndex: i,
                        closestEdge,
                        setClosestEdge,
                        draggedOverIndex,
                        setDraggedOverIndex,
                      }}
                      reorderingProps={{
                        handleReorder,
                        isRightmostTab:
                          (isSelected && isSelectedViewInOverflow) ||
                          i === visibleTabCount - 1,
                        rightmostTabIndex: visibleTabCount - 1,
                      }}
                    />
                  </Wrapper>
                );
              })}
            </TabList>
          </Tabs>
        </Box>
      </InstanceIdContext.Provider>
      <Inline ref={viewTabsActionsRef} alignBlock="center">
        {(!isReadonly || isOverflow) && (
          <Box
            backgroundColor="color.background.accent.gray.subtler"
            xcss={xcss({
              width: '1px',
              height: '20px',
              borderRadius: 'border.radius.050',
              marginRight: 'space.050',
            })}
          />
        )}
        {!isReadonly && !isOverflow && <AddViewButton handleAddView={handleAddView} />}
        {isOverflow && (
          <div data-testid="view-picker-overflow">
            <ViewPicker
              contentId={contentId}
              canEditViews={!isReadonly}
              defaultView={defaultView}
              isOpen={isViewsMenuOpen}
              setIsOpen={setIsViewsMenuOpen}
              handleAddView={handleAddView}
              openViewSettingsPanel={openViewSettingsPanel}
              showViewSettings={showViewSettings}
              tooltipContent={formatMessage(messages['view.overflow-tooltip-text'], {
                n: savedViews.length - visibleTabCount,
              })}
            />
          </div>
        )}
      </Inline>
    </Inline>
  );
};
export default ViewTabs;
